import React, { useMemo } from 'react';

/* A mind map drawn from an indented outline — what Copilot returns for
   "Mind map". Central topic on the left, branches fanning right. The outline is
   parsed leniently (any indent width, any bullet), because it comes from a
   model; anything deeper than three levels folds into the third. */

const COL = 214;
const ROW = 30;
const MAX_DEPTH = 3;
const MAX_CHARS = 34;

function parse(text, fallbackTitle) {
  const lines = (text || '').split('\n').filter((l) => l.trim());
  const strip = (l) => l.trim().replace(/^([-*•+]|\d+[.)])\s+/, '').replace(/\*\*/g, '').trim();
  const indentOf = (l) => l.replace(/\t/g, '    ').match(/^ */)[0].length;
  let first = 0;
  const root = { label: fallbackTitle || 'Document', children: [], depth: 0 };
  if (lines.length && !/^\s*([-*•+]|\d+[.)])\s/.test(lines[0])) {
    root.label = strip(lines[0]);
    first = 1;
  }
  const stack = [{ indent: -1, node: root }];
  for (const line of lines.slice(first)) {
    const indent = indentOf(line);
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) stack.pop();
    const parent = stack[stack.length - 1].node;
    const depth = Math.min(parent.depth + 1, MAX_DEPTH);
    const node = { label: strip(line), children: [], depth };
    (depth > parent.depth ? parent : stack[stack.length - 2]?.node || root).children.push(node);
    stack.push({ indent, node });
  }
  return root;
}

function layout(root) {
  const nodes = [];
  const links = [];
  let leaf = 0;
  let maxDepth = 0;
  (function place(n) {
    maxDepth = Math.max(maxDepth, n.depth);
    if (!n.children.length) {
      n.y = leaf * ROW + ROW / 2;
      leaf += 1;
    } else {
      n.children.forEach(place);
      n.y = (n.children[0].y + n.children[n.children.length - 1].y) / 2;
    }
    n.x = n.depth * COL + 8;
    nodes.push(n);
    n.children.forEach((c) => links.push([n, c]));
  })(root);
  return { nodes, links, width: (maxDepth + 1) * COL + 16, height: Math.max(leaf, 1) * ROW + 8 };
}

const clip = (s) => (s.length > MAX_CHARS ? `${s.slice(0, MAX_CHARS - 1)}…` : s);

// Rough width of bold 11–12px sans text: capitals and wide letters take more room.
function textWidth(s, depth) {
  let w = 0;
  for (const ch of s) w += /[A-Z]|[mwMW]/.test(ch) ? 8.2 : /[ilj.,:;'|! ]/.test(ch) ? 3.6 : 6.6;
  return depth === 0 ? w * 1.1 : w;
}

export default function MindMap({ text, title }) {
  const { nodes, links, width, height } = useMemo(() => layout(parse(text, title)), [text, title]);
  const w = (n) => Math.min(COL - 22, 20 + textWidth(clip(n.label), n.depth));

  return (
    <div className="mindmap" role="img" aria-label={`Mind map of ${title || 'the document'}`}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {links.map(([a, b], i) => (
          <path
            key={i}
            className={`mm-link d${b.depth}`}
            d={`M${a.x + w(a)} ${a.y} C ${a.x + w(a) + 24} ${a.y}, ${b.x - 24} ${b.y}, ${b.x} ${b.y}`}
          />
        ))}
        {nodes.map((n, i) => (
          <g key={i} className={`mm-node d${n.depth}`} transform={`translate(${n.x},${n.y - 11})`}>
            <title>{n.label}</title>
            <rect width={w(n)} height="22" rx="11" />
            <text x={w(n) / 2} y="15" textAnchor="middle">{clip(n.label)}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}
